import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import CertificateLink from '../CertificateLink';
import {
  CUSTOM_PRICE_TYPES,
  CUSTOM_WEIGHT_UNITS,
  DEFAULT_CURRENCY,
  SPECIAL_REQUEST_CATEGORIES,
  SUPPORTED_CURRENCIES,
} from '../../constants/appConstants';
import { formatCurrency } from '../../utils/currency';
import { mapCompanyToForm } from '../../utils/settingsForm';
import {
  buildManualCustomPayload,
  customItemPriceLabel,
  isPricingFormValid,
  previewWeightedTotal,
  weightUnitShort,
} from '../../utils/customItemPricing';
import { calculateClientPrice } from '../../utils/pricing';
import { resolvePricingRules } from '../../utils/pricingRules';
import { resolveFulfillOrderItemStatus, SPECIAL_REQUEST_FULFILL_ORDER_STATUSES } from '../../utils/specialRequestSettings';
import { buildOrderDescriptionFromRequest } from '../../utils/specialRequestDisplay';
import { ORDER_STATUSES } from '../../constants/appConstants';

const FULFILL_MODES = {
  INVENTORY: 'inventory',
  CUSTOM: 'custom',
};

function formatDiamondOptionLabel(diamond) {
  if (!diamond) return '';
  const parts = [
    diamond.certificateNumber,
    diamond.shape,
    diamond.carat != null ? `${diamond.carat}ct` : null,
    diamond.color,
    diamond.clarity,
  ].filter(Boolean);
  return parts.join(' · ');
}

export default function SpecialRequestFulfillDialog({
  open,
  onClose,
  request,
  loading,
  onSubmit,
}) {
  const { t } = useTranslation();
  const { company } = useAuth();
  const [fulfillMode, setFulfillMode] = useState(FULFILL_MODES.CUSTOM);
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [priceType, setPriceType] = useState(CUSTOM_PRICE_TYPES.FIXED);
  const [weightUnit, setWeightUnit] = useState(CUSTOM_WEIGHT_UNITS.CARAT);
  const [totalPrice, setTotalPrice] = useState('');
  const [weight, setWeight] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [error, setError] = useState('');

  const [certQuery, setCertQuery] = useState('');
  const [diamondOptions, setDiamondOptions] = useState([]);
  const [selectedDiamond, setSelectedDiamond] = useState(null);
  const [searchingDiamonds, setSearchingDiamonds] = useState(false);
  const [clientPricing, setClientPricing] = useState(null);
  const [inventoryPriceOverride, setInventoryPriceOverride] = useState('');
  const [fulfillOrderStatus, setFulfillOrderStatus] = useState(SPECIAL_REQUEST_FULFILL_ORDER_STATUSES.READY);

  const resetInventoryState = useCallback(() => {
    setCertQuery('');
    setDiamondOptions([]);
    setSelectedDiamond(null);
    setInventoryPriceOverride('');
  }, []);

  useEffect(() => {
    if (!open || !request) return;

    const defaultMode = request.category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED
      ? FULFILL_MODES.INVENTORY
      : FULFILL_MODES.CUSTOM;

    setFulfillMode(defaultMode);
    setDescription(buildOrderDescriptionFromRequest(request));
    setPriceType(CUSTOM_PRICE_TYPES.FIXED);
    setWeightUnit(CUSTOM_WEIGHT_UNITS.CARAT);
    setTotalPrice('');
    setWeight('');
    setPricePerUnit('');
    setError('');
    setCurrency(DEFAULT_CURRENCY);
    resetInventoryState();
    setClientPricing(null);

    const clientId = request.clientUser?._id || request.clientUser;
    if (clientId) {
      axios.get(`/api/users/${clientId}`)
        .then((res) => setClientPricing(res.data))
        .catch(() => setClientPricing(null));
    }

    const companyId = company?.companyId;
    if (!companyId) return;
    axios.get(`/api/companies/${companyId}`)
      .then((res) => {
        const mapped = mapCompanyToForm(res.data);
        if (mapped.defaultItemCurrency) setCurrency(mapped.defaultItemCurrency);
        setFulfillOrderStatus(
          mapped.specialRequestSettings?.fulfillOrderStatus || SPECIAL_REQUEST_FULFILL_ORDER_STATUSES.READY,
        );
      })
      .catch(() => {});
  }, [open, request, company?.companyId, resetInventoryState]);

  const fulfillHintKey = resolveFulfillOrderItemStatus({ specialRequestSettings: { fulfillOrderStatus } })
    === ORDER_STATUSES.MEMO
    ? 'specialRequests.fulfillHintDelivered'
    : 'specialRequests.fulfillHintReady';

  useEffect(() => {
    if (!open || fulfillMode !== FULFILL_MODES.INVENTORY) return undefined;
    const query = certQuery.trim();
    if (query.length < 2) {
      setDiamondOptions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearchingDiamonds(true);
      try {
        const res = await axios.get('/api/diamonds', {
          params: {
            certificateNumber: query,
            status: 'available',
            limit: 15,
          },
        });
        setDiamondOptions(res.data?.diamonds || []);
      } catch {
        setDiamondOptions([]);
      } finally {
        setSearchingDiamonds(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [open, fulfillMode, certQuery]);

  const currencyMeta = SUPPORTED_CURRENCIES.find((c) => c.code === currency);
  const currencySymbol = currencyMeta?.symbol || currency;
  const weightedPreview = previewWeightedTotal(weight, pricePerUnit);

  const inventoryPricePerCarat = useMemo(() => {
    if (!selectedDiamond) return null;
    const override = inventoryPriceOverride !== '' ? Number(inventoryPriceOverride) : null;
    if (override != null && Number.isFinite(override) && override >= 0) return override;
    if (!clientPricing) return Number(selectedDiamond.basePrice) || null;
    const rules = resolvePricingRules(clientPricing);
    return calculateClientPrice(
      selectedDiamond.basePrice,
      selectedDiamond.carat,
      { pricingRule: rules.certified },
    );
  }, [selectedDiamond, clientPricing, inventoryPriceOverride]);

  const inventoryTotal = useMemo(() => {
    if (!selectedDiamond || inventoryPricePerCarat == null) return null;
    const carat = Number(selectedDiamond.carat);
    if (!Number.isFinite(carat)) return null;
    return Math.round(carat * inventoryPricePerCarat * 100) / 100;
  }, [selectedDiamond, inventoryPricePerCarat]);

  const canSubmitInventory = !loading
    && selectedDiamond
    && selectedDiamond.status === 'available'
    && inventoryPricePerCarat != null
    && inventoryPricePerCarat >= 0;

  const canSubmitCustom = !loading
    && description.trim()
    && isPricingFormValid({ priceType, weightUnit, weight, pricePerUnit, totalPrice });

  const canSubmit = fulfillMode === FULFILL_MODES.INVENTORY ? canSubmitInventory : canSubmitCustom;

  const handleSubmit = async () => {
    setError('');
    try {
      if (fulfillMode === FULFILL_MODES.INVENTORY) {
        const payload = { diamondId: selectedDiamond._id };
        if (inventoryPriceOverride !== '' && Number(inventoryPriceOverride) >= 0) {
          payload.pricePerUnit = Number(inventoryPriceOverride);
        }
        await onSubmit(payload);
        return;
      }

      const pricingPayload = buildManualCustomPayload({
        description,
        currency,
        priceType,
        weightUnit,
        weight,
        pricePerUnit,
        totalPrice,
      });
      await onSubmit(pricingPayload);
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('specialRequests.fulfillTitle')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t(fulfillHintKey)}
          </Typography>

          <ToggleButtonGroup
            value={fulfillMode}
            exclusive
            size="small"
            fullWidth
            disabled={loading}
            onChange={(_e, next) => {
              if (!next) return;
              setFulfillMode(next);
              setError('');
              if (next === FULFILL_MODES.CUSTOM) resetInventoryState();
            }}
          >
            <ToggleButton value={FULFILL_MODES.INVENTORY}>
              {t('specialRequests.fulfillModeInventory')}
            </ToggleButton>
            <ToggleButton value={FULFILL_MODES.CUSTOM}>
              {t('specialRequests.fulfillModeCustom')}
            </ToggleButton>
          </ToggleButtonGroup>

          {fulfillMode === FULFILL_MODES.INVENTORY && (
            <>
              <Autocomplete
                size="small"
                options={diamondOptions}
                value={selectedDiamond}
                onChange={(_e, value) => setSelectedDiamond(value)}
                onInputChange={(_e, value) => setCertQuery(value)}
                getOptionLabel={formatDiamondOptionLabel}
                isOptionEqualToValue={(a, b) => a?._id === b?._id}
                loading={searchingDiamonds}
                filterOptions={(x) => x}
                noOptionsText={
                  certQuery.trim().length < 2
                    ? t('specialRequests.searchCertificateHint')
                    : t('specialRequests.diamondNotFound')
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('specialRequests.searchByCertificate')}
                    placeholder={t('specialRequests.searchCertificatePlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchingDiamonds ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              {selectedDiamond && (
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('specialRequests.selectedDiamond')}
                  </Typography>
                  <CertificateLink
                    certificateNumber={selectedDiamond.certificateNumber}
                    lab={selectedDiamond.lab}
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                  />
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {[
                      selectedDiamond.shape,
                      selectedDiamond.carat != null ? `${selectedDiamond.carat} ct` : null,
                      selectedDiamond.color,
                      selectedDiamond.clarity,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                  {inventoryTotal != null && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {t('specialRequests.clientPricePreview')}:{' '}
                      <strong>{formatCurrency(inventoryTotal, currency)}</strong>
                      {inventoryPricePerCarat != null && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({formatCurrency(inventoryPricePerCarat, currency)}/ct)
                        </Typography>
                      )}
                    </Typography>
                  )}
                </Box>
              )}

              <TextField
                label={t('specialRequests.pricePerCaratOverride')}
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={inventoryPriceOverride}
                onChange={(e) => setInventoryPriceOverride(e.target.value)}
                fullWidth
                size="small"
                disabled={loading || !selectedDiamond}
                helperText={t('specialRequests.pricePerCaratOverrideHelp')}
              />
            </>
          )}

          {fulfillMode === FULFILL_MODES.CUSTOM && (
            <>
              <TextField
                label={t('specialRequests.orderDescription')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                required
                multiline
                minRows={2}
                disabled={loading}
              />

              <TextField
                select
                label={t('settings.sections.currency')}
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                fullWidth
                required
                disabled={loading}
                size="small"
              >
                {SUPPORTED_CURRENCIES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </MenuItem>
                ))}
              </TextField>

              <ToggleButtonGroup
                value={priceType}
                exclusive
                size="small"
                fullWidth
                disabled={loading}
                onChange={(_e, next) => { if (next) setPriceType(next); }}
              >
                <ToggleButton value={CUSTOM_PRICE_TYPES.FIXED}>{t('specialRequests.pricingFixed')}</ToggleButton>
                <ToggleButton value={CUSTOM_PRICE_TYPES.BY_WEIGHT}>{t('specialRequests.pricingByWeight')}</ToggleButton>
              </ToggleButtonGroup>

              {priceType === CUSTOM_PRICE_TYPES.FIXED && (
                <TextField
                  label={customItemPriceLabel('total', currency, currencySymbol)}
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  value={totalPrice}
                  onChange={(e) => setTotalPrice(e.target.value)}
                  fullWidth
                  required
                  disabled={loading}
                />
              )}

              {priceType === CUSTOM_PRICE_TYPES.BY_WEIGHT && (
                <>
                  <ToggleButtonGroup
                    value={weightUnit}
                    exclusive
                    size="small"
                    fullWidth
                    disabled={loading}
                    onChange={(_e, next) => { if (next) setWeightUnit(next); }}
                  >
                    <ToggleButton value={CUSTOM_WEIGHT_UNITS.CARAT}>{t('specialRequests.weightCarat')}</ToggleButton>
                    <ToggleButton value={CUSTOM_WEIGHT_UNITS.GRAM}>{t('specialRequests.weightGram')}</ToggleButton>
                  </ToggleButtonGroup>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label={`${t('specialRequests.weight')} (${weightUnitShort(weightUnit)})`}
                      type="number"
                      inputProps={{ min: 0.0001, step: 0.01 }}
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      fullWidth
                      required
                      disabled={loading}
                    />
                    <TextField
                      label={`${customItemPriceLabel('perUnit', currency, currencySymbol)}/${weightUnitShort(weightUnit)}`}
                      type="number"
                      inputProps={{ min: 0.01, step: 0.01 }}
                      value={pricePerUnit}
                      onChange={(e) => setPricePerUnit(e.target.value)}
                      fullWidth
                      required
                      disabled={loading}
                    />
                  </Stack>
                  {weightedPreview != null && (
                    <Typography variant="body2" color="text.secondary">
                      {t('specialRequests.total')}: <strong>{formatCurrency(weightedPreview, currency)}</strong>
                    </Typography>
                  )}
                </>
              )}
            </>
          )}

          {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {loading ? t('common.saving') : t('specialRequests.actions.createOrder')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
